/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {useFormikContext} from 'formik';
import React, {useEffect, useRef} from 'react';
import {BeamMultiSelect} from '../../common/formik/BeamMultiSelect';
import {Organization} from '../../../shared/entities/organization';
import {TerminalField} from './OrganizationForm';
import {isEqual} from 'lodash';

export interface Props {
  terminals: TerminalField[];
  name: string;
  label: string;
  placeholder: string;
  currentValue: string[];
}

export const TerminalAssignmentsField = ({ terminals, name, label, placeholder, currentValue }: Props) => {
  const { values, setFieldValue } = useFormikContext<Organization>();
  let customerGroupInput = values?.customerGroups ?? [];
  const prevCusRef = useRef(currentValue);

  useEffect(() => {
    // case where customerGroup was changed without leaving the form: avoid terminals added to wrong customer
    const prev = prevCusRef.current;

    if (!isEqual(prev, currentValue)) {
      setFieldValue('terminalAssignments',
          values.terminalAssignments.filter(t => currentValue.includes(t.customerGroup)));
    }

    prevCusRef.current = currentValue;
  }, [setFieldValue, currentValue]);

  const filterTerminalsForCustomerGroupInput = () => {
    return terminals
        .filter(t => customerGroupInput.includes(t.customerGroup))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  if (customerGroupInput.length === 0) {
    // return an empty set - hide terminal field
    return <></>;
  }

  return (
    <BeamMultiSelect
      name={name}
      label={label}
      options={filterTerminalsForCustomerGroupInput()}
      getOptionLabel={option => option.displayName}
      placeholder={placeholder}
      isOptionEqualToValue={(option, value) => option.displayName === value.displayName}
    />
  );
};
